import React, {ReactElement} from 'react';
import {graphql, PageProps} from 'gatsby';
import {cx} from '@emotion/css';

import SEO from '../components/seo';
import {LinkProp, Hero, ContentfulContent} from '../types';
import KnowledgeLayout from '../components/DesignSystem/Layout/KnowledgeLayout/KnowledgeLayout';
import heroImage from '../images/Hero/knowledgePlaceholder.jpg';
import RelatedArticles from '../components/DesignSystem/RelatedArticles/RelatedArticles';
import {Locale} from '../RootProvider/LocaleContext';
import getLinksFromContent from '../contentful/getLinksFromContent';
import getRelatedArticlesLinks from '../contentful/getRelatedArticlesLinks';
import NotFoundPage from '../pages/404';
import RichText from '../components/DesignSystem/Text/RichText/RichText';

import getCanonicalUrl from '../utils/getCanonicalUrl';
import {getPageRoute} from '../utils/routes/getPageRoute';
import useHandleAffid from '../useHandleAffid';
import addRefsToRichText from '../components/DesignSystem/Text/RichText/addRefsToRichText';

import styles from './KnowledgePage.styles';

export const query = graphql`
  query KnowledgePage($slug: String!, $locale: String!) {
    contentfulKnowledgePage(slug: {eq: $slug}, node_locale: {eq: $locale}) {
      name
      description
      slug
      customCtaText
      hero {
        image {
          file {
            url
          }
        }
      }
      heroTitle
      heroDescription {
        heroDescription
      }
      content {
        raw
        references {
          ... on ContentfulAsset {
            contentful_id
            title
            description
            file {
              url
            }
          }
          ... on ContentfulAccordion {
            __typename
            accordionName
            contentful_id
            node_locale
            items {
              label
              content {
                raw
              }
            }
          }
        }
      }
      relatedArticles {
        raw
      }
      robots
    }
  }
`;

interface KnowledgePageData {
  contentfulKnowledgePage?: {
    slug: string;
    name: string;
    description: string;
    hero: Hero;
    heroTitle?: string;
    heroDescription?: {
      heroDescription?: string;
    };
    customCtaText?: string;
    content?: ContentfulContent;
    relatedArticles?: Partial<ContentfulContent>;
    robots?: string;
  };
}

export interface Props extends PageProps<KnowledgePageData, {locale: Locale}> {}

function KnowledgePage({data: pageData, pageContext}: Props): ReactElement {
  useHandleAffid();
  if (!pageData || !pageData.contentfulKnowledgePage) {
    return <NotFoundPage />;
  }

  const {content, customCtaText, description, hero, heroDescription, heroTitle, name, relatedArticles, robots, slug} =
    pageData.contentfulKnowledgePage;

  const contentWithRefs = content && addRefsToRichText(content);

  if (!contentWithRefs) {
    return <NotFoundPage />;
  }

  const {locale} = pageContext;

  const anchorsLinks: LinkProp[] = (contentWithRefs && getLinksFromContent(contentWithRefs.content)) || [];
  const relatedArticlesLinks: LinkProp[] = (relatedArticles && getRelatedArticlesLinks(relatedArticles)) || [];

  return (
    <KnowledgeLayout
      customCtaText={customCtaText}
      image={hero?.image?.file?.url || heroImage}
      links={anchorsLinks}
      heroTitle={heroTitle || ''}
      heroDescription={heroDescription?.heroDescription || ''}
    >
      <SEO
        title={name}
        description={description}
        meta={robots ? [{name: 'robots', content: robots}] : undefined}
        link={[
          {
            rel: 'canonical',
            href: getCanonicalUrl(getPageRoute('knowledge.article', locale, {contentfulSlug: slug})),
          },
        ]}
      />
      <RichText className={cx(styles.richText)} content={content} />
      {relatedArticlesLinks.length > 0 && (
        <RelatedArticles className={cx(styles.relatedArticles)} links={relatedArticlesLinks} />
      )}
    </KnowledgeLayout>
  );
}

export default KnowledgePage;
